import type { ReactNode } from 'react';

import { confidenceBandLabel } from './findings';
import type { ResolvableItem } from './resolutionQueue';
import { Evidence } from './Evidence';
import { ReferencePicker, type ReferencePickerProps } from './ReferencePicker';
import styles from './review.module.css';
import { Icon } from '../ui/Icon';
import { Sheet } from '../ui/Sheet';

function confidenceBars(band: string | null): number {
  if (band === 'high') return 3;
  if (band === 'medium') return 2;
  if (band === 'low') return 1;
  return 0;
}

/** Zero-based place of this field in the walk the owner is currently on, for the `K de N`
 * header. `null` when the sheet was opened on its own - a resolved field's chevron - and so
 * belongs to no walk. */
export interface ResolutionPosition {
  readonly index: number;
  readonly total: number;
}

/**
 * What the sheet offers once the current field has an answer. The owner decides when to move
 * on: nothing advances by itself, because a walk that jumps the moment a value is picked gives
 * no chance to read back what was chosen, confirm an alias, or undo a mis-tap.
 */
export interface ResolutionStep {
  /** What just happened, in the owner's words - "Resuelto: X" or the marked-as-new wording. */
  readonly summary: string;
  readonly nextLabel: string;
  readonly onNext: () => void;
  /** Offered beside `onNext` at the end of a walk, when another note still needs resolving. */
  readonly secondary?: { readonly label: string; readonly onClick: () => void } | undefined;
}

export interface ResolutionSheetProps {
  readonly item: ResolvableItem;
  readonly position: ResolutionPosition | null;
  readonly noteId: string;
  readonly onClose: () => void;
  readonly onShowFullImage: () => void;
  /** Non-null only while a walk is waiting on the owner to continue (see `ResolutionStep`). */
  readonly step: ResolutionStep | null;
  readonly pickerProps: Omit<ReferencePickerProps, 'kind'> & {
    readonly kind: ReferencePickerProps['kind'];
  };
  /** An action that must remain part of the active modal, rather than the hidden review page. */
  readonly footer?: ReactNode;
}

/**
 * The per-field resolution flow (redesign §Revisar nota): bottom sheet on a phone, a fixed
 * right-side panel on a desktop-width viewport (`ui/Sheet.tsx`'s `variant="resolution"` handles
 * that shell switch in CSS alone, so this component and its state are identical either way).
 * Reuses `Evidence.tsx` verbatim for "En la foto dice" (literal text, cropped image, and any
 * recognition-time findings - CLAUDE.md "show the evidence") and `ReferencePicker` verbatim for
 * the candidate list, search, alias, and "es nuevo" affordances; this only adds the K/N header,
 * the 3-bar confidence meter the mockup asks for around them, and the explicit "continue"
 * control that ends each step of a walk.
 */
export function ResolutionSheet({
  item,
  position,
  noteId,
  onClose,
  onShowFullImage,
  step,
  pickerProps,
  footer,
}: ResolutionSheetProps) {
  const prediction = pickerProps.prediction;
  const bars = confidenceBars(prediction?.confidence_band ?? null);

  return (
    <Sheet
      title={item.label}
      variant="resolution"
      onClose={onClose}
      footer={footer}
      headerExtra={
        position === null ? undefined : (
          <span className={styles.resolutionPosition}>
            {`${String(position.index + 1)} de ${String(position.total)}`}
          </span>
        )
      }
    >
      <p className={styles.resolutionEvidenceLabel}>En la foto dice</p>
      <Evidence
        noteId={noteId}
        prediction={prediction}
        fieldLabel={item.label}
        onShowFullImage={onShowFullImage}
      />
      {prediction?.confidence_band != null ? (
        <div className={styles.confidenceMeter} aria-hidden="true">
          {[1, 2, 3].map((bar) => (
            <span
              key={bar}
              className={[styles.confidenceBar, bar <= bars ? styles.confidenceBarFilled : null]
                .filter(Boolean)
                .join(' ')}
            />
          ))}
          <span className={styles.confidenceMeterLabel}>
            {confidenceBandLabel(prediction.confidence_band)}
          </span>
        </div>
      ) : null}

      {step !== null ? (
        <div className={styles.resolutionStep} role="status">
          <p className={styles.resolutionStepSummary}>
            <Icon
              name="check-circle"
              size={16}
              strokeWidth={2.2}
              className={styles.resolutionStepIcon}
            />
            {step.summary}
          </p>
          <button type="button" className={styles.primaryButton} onClick={step.onNext}>
            {step.nextLabel}
          </button>
          {step.secondary !== undefined ? (
            <button type="button" className={styles.linkButton} onClick={step.secondary.onClick}>
              {step.secondary.label}
            </button>
          ) : null}
        </div>
      ) : null}

      <p className={styles.resolutionQuestion}>
        {item.kind === 'customer'
          ? '¿A qué cliente de Excel corresponde?'
          : '¿A qué concepto de Excel corresponde?'}
      </p>
      <ReferencePicker {...pickerProps} />
    </Sheet>
  );
}
